import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Flex, Modal, Tabs } from 'antd';
import { useSelector } from 'react-redux';
import { FilePdfOutlined, FormOutlined, HistoryOutlined } from '@ant-design/icons';
import { collect } from 'collect.js';
import useForm from '../../../hooks/utility/useForm';
import LoadingScreen from '../../../components/loading/LoadingScreen';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import useApiClient from '../../../hooks/api/useApiClient';
import BusinessInfo from '../../../components/contract/BusinessInfo';
import PriceInfo from '../../../components/contract/PriceInfo';
import usePermissions, { ManagerRole } from '../../../hooks/auth/usePermissions';
import { BillingMode, OnboardingStep, SubscriptionTerm } from '../../../services/exports/Constants';
import SignedContract from '../../../components/contract/SignedContract';
import ContractPayment from '../../../components/contract/ContractPayment';
import SignStepStrategy from '../../../components/contract/SignStepStrategy';
import SignatureHistory from '../../../components/contract/SignatureHistory';
import SwitchToAnnualFlow from '../../../components/contract/SwitchToAnnualFlow';
import useVerification from '../../../hooks/verification/useVerification';

export const info_tab = 'info';
export const contract_tab = 'contract';
export const versioning_tab = 'versioning';

export default function ContractInfoView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Contract:Agreement' });

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { ServiceContractsManager } = useApiClient();

  const { isMerchant, hasAnyRole } = usePermissions();

  const { isContractLocked } = useVerification();

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contractLoaded, setContractLoaded] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSwitchToAnnualModal, setShowSwitchToAnnualModal] = useState(false);
  const { setFeedback, renderFeedbackLabel } = useFeedbackLabel();

  // Switching to annual only applies to an already-signed contract that is not
  // yet on an annual commitment term.
  const canSwitchToAnnual =
    hasAnyRole([
      ManagerRole.SalesManager,
      ManagerRole.SalesLeadership,
      ManagerRole.Reseller,
      ManagerRole.OnboardingManager,
    ]) &&
    serviceProvider?.has_merchant_contracts &&
    Boolean(contract?.signed_at || contract?.merchant?.skipped_verification_steps?.includes(OnboardingStep.Contract)) &&
    contract?.contract_term !== SubscriptionTerm.Annual;
  const setupPackages = serviceProvider.setup_packages;
  const hasSetupFee = useMemo(
    () =>
      contract?.single_store_setup_fee > 0 ||
      collect(setupPackages)
        .filter((item) => item.stripe_billing_price !== 0 || item.internal_invoice_billing_price !== 0)
        .isNotEmpty(),
    [setupPackages, contract?.single_store_setup_fee],
  );

  const { data, setData, update } = useForm({
    company_name: contract?.company_name,
    company_phone_number: contract?.company_phone_number,
    business_name: contract?.business_name,
    business_address: contract?.business_address,
    business_zip_code: contract?.business_zip_code,
    business_city: contract?.business_city,
    business_country: contract?.business_country,
    business_tax_id: contract?.business_tax_id,
    owner_full_name: contract?.owner_full_name,
    contact_person_full_name: contract?.contact_person_full_name,
    contact_person_phone_number: contract?.contact_person_phone_number,
    contact_person_email: contract?.contact_person_email,

    business_bank_account_holder_name: contract?.business_bank_account_holder_name,
    business_bank_account_number: contract?.business_bank_account_number,
    business_bank_routing_number: contract?.business_bank_account_number,

    store_quantity: contract?.store_quantity ?? 1,
    setup_package_id: contract?.setup_package_id ?? null,
    billing_mode: contract?.billing_mode ?? BillingMode.Stripe,
    netto_mobile_app_setup_fee: contract?.netto_mobile_app_setup_fee ?? 0,
  });

  useEffect(() => {
    loadContract();
  }, []);

  const loadContract = async () => {
    setLoading(true);
    const { success, data } = await ServiceContractsManager.get();
    setLoading(false);

    if (!success) {
      setContractLoaded(true);

      return toast.error(t('toasts.failed_to_load_data'));
    }

    if (!data) {
      setContractLoaded(true);

      return;
    }

    setContract({
      ...data,
      has_trial: data.trial_days > 0,
    });
    setData({
      ...data,
      has_trial: data.trial_days > 0,
      netto_mobile_app_setup_fee:
        data.netto_mobile_app_setup_fee !== null ? data.netto_mobile_app_setup_fee : undefined,
    });

    setContractLoaded(true);
  };

  const save = async (extra = {}) => {
    setSaving(true);
    const { success, data: responseData } = await ServiceContractsManager.save({ ...data, ...extra });
    setSaving(false);

    if (!success) {
      setFeedback({
        message: responseData?.message,
        type: 'error',
        errors: responseData?.errors,
      });

      return toast.error(t('toasts.failed_to_save_changes'));
    }

    return toast.success(t('toasts.changes_got_saved'));
  };

  const onSetupPackageSelection = (setupPackage) => {
    update('setup_package_id', setupPackage.id);

    return save({ setup_package_id: setupPackage.id });
  };

  const onContractSigned = (data) => {
    setContract(data);
    setShowPreviewModal(false);
  };

  const onSwitchedToAnnual = (data) => {
    if (data) {
      setContract({
        ...data,
        has_trial: data.trial_days > 0,
      });
    }

    setShowSwitchToAnnualModal(false);
  };

  const tabs = [
    {
      key: info_tab,
      label: t(`tabs.${info_tab}`),
      icon: <FormOutlined />,
      disabled: isMerchant,
      children: (
        <>
          <h2 className="mb-lg">{t('title')}</h2>
          {renderFeedbackLabel}
          {contractLoaded && !contract?.signed_at && hasSetupFee && (
            <PriceInfo
              data={data}
              loading={saving}
              setupPackages={setupPackages}
              onChange={update}
              onSetupPackageSelection={onSetupPackageSelection}
            />
          )}
          <div className="mt-lg">
            <BusinessInfo data={data} hasSetupFee={hasSetupFee} onChange={update}>
              <Flex justify="flex-end">
                <Button type="primary" loading={saving} onClick={() => save()}>
                  {t('buttons.save')}
                </Button>
              </Flex>
            </BusinessInfo>
          </div>
        </>
      ),
    },
    ...(isContractLocked
      ? [
          {
            key: contract_tab,
            label: t(`tabs.${contract_tab}`),
            icon: <FilePdfOutlined />,
            children: (
              <>
                <Flex justify="space-between">
                  <h2 className="mb-lg">{t('title')}</h2>
                  <Flex gap="small">
                    {canSwitchToAnnual && (
                      <Button onClick={() => setShowSwitchToAnnualModal(true)}>{t('buttons.switch_to_annual')}</Button>
                    )}
                    {contract?.signed_at && contract?.file_url ? (
                      <Button type="primary" onClick={() => setShowPreviewModal(true)}>
                        {t('buttons.preview_and_sign')}
                      </Button>
                    ) : null}
                  </Flex>
                </Flex>
                <Card
                  title={t('sections.contact')}
                  extra={
                    <Button
                      disabled={!contract?.signed_at || !contract?.file_url}
                      onClick={() => window.open(contract?.file_url, '_blank')}
                    >
                      {t('buttons.download')}
                    </Button>
                  }
                  className="overflow-auto"
                >
                  {contract?.signed_at ? (
                    <>
                      <SignedContract data={contract} />
                      {contract?.is_immediate_payment_required && (
                        <>
                          <hr className="my-lg w-full" />
                          <ContractPayment data={contract} />
                        </>
                      )}
                    </>
                  ) : (
                    <Flex justify="center">
                      <div className="flex flex-col">
                        <h4>{t('labels.not_signed')}</h4>
                        <Button type="primary" onClick={() => setShowPreviewModal(true)} className="mt-md mx-auto">
                          {t('buttons.preview_and_sign')}
                        </Button>
                      </div>
                    </Flex>
                  )}
                </Card>
                {showPreviewModal && (
                  <Modal
                    open
                    centered
                    title={t('modals.preview_contract.title')}
                    onCancel={() => setShowPreviewModal(false)}
                    footer={null}
                    className="!min-w-[max(600px,50%)]"
                  >
                    <SignStepStrategy contract={contract} onSuccess={onContractSigned} />
                  </Modal>
                )}
                {showSwitchToAnnualModal && (
                  <Modal
                    open
                    centered
                    title={t('modals.switch_to_annual.title')}
                    onCancel={() => {
                      setShowSwitchToAnnualModal(false);
                      loadContract();
                    }}
                    footer={null}
                    className="!min-w-[max(600px,60dvw)] !top-0 !max-w-full !p-0 !m-0 min-h-[80dvh]"
                  >
                    <SwitchToAnnualFlow contract={contract} onSuccess={onSwitchedToAnnual} />
                  </Modal>
                )}
              </>
            ),
          },
        ]
      : []),
    {
      key: versioning_tab,
      label: t(`tabs.${versioning_tab}`),
      icon: <HistoryOutlined />,
      disabled: isMerchant,
      children: (
        <>
          <h2 className="mb-lg">{t('sections.signature_history')}</h2>
          <Card className="overflow-auto">
            <SignatureHistory history={contract?.signature_history} />
          </Card>
        </>
      ),
    },
  ];

  return loading ? <LoadingScreen /> : <Tabs defaultActiveKey={contract_tab} items={tabs} />;
}
